import { useMutation, useLazyQuery, useQuery } from '@apollo/client';
import {
  AppContext,
  getTranslatedText,
  NoData,
  Loader,
  Emoji,
  DateTimeFormatter,
  isAuthorised,
  COMMON_CONSTANTS
} from '@nile/components';
import {
  AccountClient,
  GET_PERSONALIZATION_BY_NAME,
  SAVE_PERSONALIZATION,
  useCachedQuery,
  GET_APP_PERSONALIZATION
} from '@nile/graphql';
import envConfig from '@nile/home/src/Config/env';
import {
  DEFAULT_LAYOUT,
  ROW_HEIGHT
} from '@nile/mangifera/src/Constants/homepage';
import HomeWidgetContainer from '@nile/mangifera/src/Containers/HomeWidgetContainer';
import WidgetLibrary from '@nile/mangifera/src/Containers/WidgetLibrary';
import { Button, Avatar, Sheet, ButtonGroup } from '@superops/ui-design-system';
import { cloneDeep, isEmpty } from 'lodash';
import React, { createRef, useCallback, useContext, useState } from 'react';
import { getDimensions } from '@nile/reports/src/modules/charts/utils/layoutHelper';
import { FormattedMessage, useIntl } from 'react-intl';
import { withRouter } from 'react-router-dom';
import styled from 'styled-components';
import { SVGIcon } from '@superops/super-icons';
import TabContext from '@nile/mangifera/src/Contexts/TicketContext';
import { AIOnboardingHook } from '@nile/components/src/Components/AI/hooks/AIOnboardingHook';

const NoDataWrapper = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const WidgetExplorer = styled.div`
  height: calc(100% - 100px);
`;

const Header = styled.div`
  height: 100px;
  border-bottom: 1px solid #e7e7e7;
`;

const Actions = styled.div`
  margin-left: auto;
  display: flex;
`;

const HomeScreen = styled.div`
  background: var(--sops-lavendar-to-black-bg);
  /* padding: 20px; */
  height: 100%;
  .module-logo {
    height: 56px;
    width: 64px;
  }

  .columnHeader {
    flex: none !important;
  }

  .tbody {
    display: inline-block;
    max-height: 64vh;
    overflow: auto;

    .tr {
      height: 42px;
    }

    padding-top: 12px;
  }

  .tr .td,
  .inblock {
    padding-left: 4px;
  }

  .unFreezeContent {
    width: 100%;
    overflow: auto;
    white-space: nowrap;
  }
`;
const Greeting = styled.div`
  line-height: 60px;
  font-weight: 300;
  font-size: 30px;
  /* margin-bottom: 25px; */
  padding: 0 10px;
  column-gap: 20px;
  .Superops-Avatar {
    width: 60px;
    height: 60px;
  }
`;
const Message = styled.div.attrs({ className: 'grid' })`
  h4 {
    font-weight: 600;
    font-size: 20px;
    line-height: 30px;
    color: rgba(0, 0, 0, 0.85);
  }
`;
const UpdateTime = styled.div`
  font-weight: 400;
  font-size: 14px;
  line-height: 14px;
  color: rgba(0, 0, 0, 0.5);
  .time {
    color: rgba(0, 0, 0, 0.8);
  }
`;

const holderRef = createRef();

function HomePage(props) {
  const intl = useIntl();

  const appContext = useContext(AppContext);
  const tabContext = useContext(TabContext);
  const { user } = appContext;
  const reference = user?.role?.roleFeatureList || [];
  const aiOnboardingHook = AIOnboardingHook();

  const updatedTime = new Date();
  const modulePermission = {
    TICKET: isAuthorised(reference, 'tickets.view'),
    TASK: isAuthorised(reference, 'taskViews.view'),
    PROJECT: isAuthorised(reference, 'project.view'),
    ALERT: isAuthorised(reference, 'alertView.view'),
    ASSET_VIEWS: isAuthorised(reference, 'assetViews.view'),
    PATCH: isAuthorised(reference, 'patchViews.view'),
    ReportWidget: isAuthorised(reference, 'report.view')
  };
  const greetText = 'Good day';

  const [widgetLibraryActive, setWidgetLibraryActive] = useState(false);
  const [homeState, setHomeState] = useState({
    layout: []
  });

  const toggleWidgetLibrary = useCallback(() =>
    setWidgetLibraryActive(!widgetLibraryActive)
  );

  const enablePersonalization = true;

  const checkPermission = (wdt) => {
    const key = wdt?.widget?.module ?? wdt?.widget?.type;
    if (wdt?.widget?.type === 'VIEW' && wdt?.widget?.id === '1') {
      return modulePermission.TICKET;
    }
    return modulePermission[key];
  };

  const getName = (wdt) => {
    if (wdt?.widget?.type === 'VIEW' && wdt?.widget?.id === '1') {
      return 'All Ticket';
    }
    return wdt?.widget?.name;
  };

  const filteredState = (wdgts) => {
    const widgetPermission = wdgts?.layout?.map((wdt) => {
      return {
        ...wdt,
        widget: {
          ...wdt?.widget,
          name: getName(wdt),
          permission: Boolean(checkPermission(wdt))
        }
      };
    });
    setHomeState({
      layout: widgetPermission
    });
  };

  const [
    getAppPersonalization,
    { loading: appPersonalizationLoading, error: appPersonalizationError }
  ] = useLazyQuery(GET_APP_PERSONALIZATION, {
    client: AccountClient,
    fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NETWORK_ONLY,
    variables: {
      input: {
        name: 'HOME_DASHBOARD'
      }
    },
    onCompleted: (response) => {
      if (response?.getAppPersonalization?.value) {
        const formatLayout = JSON.parse(response?.getAppPersonalization?.value);
        filteredState({ ...formatLayout.data });
      } else {
        filteredState({ layout: DEFAULT_LAYOUT });
      }
    }
  });

  const {
    loading: homePersonalizationLoading,
    error: homePersonalizationError
  } = useQuery(GET_PERSONALIZATION_BY_NAME, {
    variables: {
      userId: user.userId,
      name: 'homePersonalization',
      category: 'homePersonalization'
    },
    client: AccountClient,
    fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE,
    onCompleted(res) {
      if (res?.getPersonalizationByCategoryAndName?.value) {
        filteredState({
          ...JSON.parse(res?.getPersonalizationByCategoryAndName?.value)
        });
      } else {
        getAppPersonalization();
      }
    }
  });

  const [sethomePersonalization] = useMutation(SAVE_PERSONALIZATION, {
    client: AccountClient
  });

  const addWidget = async (wdg, reset = false) => {
    const dimensions = await getDimensions({
      widget: wdg,
      layout: homeState?.layout
    });
    const updatedLayout = cloneDeep(homeState?.layout);
    updatedLayout.push({
      ...dimensions,
      widget: { ...wdg, permission: true }
    });
    setHomeState({
      // layout: reset ? [] : updatedLayout
      layout: updatedLayout
    });
    setTimeout(() => {
      holderRef.current.scrollTop =
        holderRef.current.scrollHeight + 4 * ROW_HEIGHT;
    }, 200);
  };

  const deleteWidget = (wdg) => {
    const updatedLayout = cloneDeep(homeState?.layout);
    let index;
    if (wdg.hasOwnProperty('type') && wdg?.type === 'ReportWidget') {
      index = updatedLayout.findIndex(
        (el) => el.widget?.chartId === wdg.chartId
      );
    } else {
      index = updatedLayout.findIndex((el) => el.widget?.id === wdg.id);
    }
    if (index > -1) {
      updatedLayout.splice(index, 1);
      setHomeState({
        layout: updatedLayout
      });
    }
  };

  const onLayoutChange = (l) => {
    let updatedLayout = cloneDeep(homeState?.layout);
    updatedLayout = updatedLayout.map((el) => {
      const currWidget = l.find((w) => w.i === el.i);
      return {
        x: currWidget.x,
        y: currWidget.y,
        w: currWidget.w,
        h: currWidget.h,
        i: currWidget.i,
        widget: el.widget
      };
    });
    if (enablePersonalization) {
      const personalization = {
        user: {
          firstName: user.firstName,
          userId: user.userId,
          email: user.email,
          lastName: user.lastName
        },
        category: 'homePersonalization',
        value: JSON.stringify({
          layout: updatedLayout
        })
      };
      sethomePersonalization({
        variables: { ...personalization, name: 'homePersonalization' },
        input: { ...personalization, name: 'homePersonalization' }
      });
    }
    setHomeState({
      layout: updatedLayout
    });
  };

  if (homePersonalizationLoading || appPersonalizationLoading)
    return <Loader />;
  if (homePersonalizationError || appPersonalizationError)
    return <div>error...</div>;

  return (
    <HomeScreen id="homePage">
      <Header className="flex actr p20 bg-white">
        <Greeting className="sops-greating-text fx">
          <Avatar name={user.name} initials={user.name[0]} size="medium" />
          <Message>
            <h4 className="fx">
              {`${greetText}, ${user.name}`}
              <div className="ml-5 emoji">
                <Emoji symbol="👋" label="my_tasks" />
              </div>
            </h4>
            <UpdateTime>
              {getTranslatedText(intl, 'admin.home.lastUpdatedAt')}
              <span className="time pl5">
                <DateTimeFormatter type="time" date={updatedTime} />
              </span>
            </UpdateTime>
          </Message>
        </Greeting>
        <Actions id="homeActions">
          <Button
            id="settingsBtn"
            primary
            onClick={() => toggleWidgetLibrary()}
            customClassName="homeSettingsBtn"
            icon={<SVGIcon name="ChartLibrary" color="#fff" />}
          >
            <span id="widget-library">
              <FormattedMessage id="admin.home.widgetlibrary" />
            </span>
          </Button>
        </Actions>
      </Header>
      <div></div>

      {isEmpty(homeState?.layout) ? (
        <HomeNoData intl={intl} />
      ) : (
        <WidgetExplorer className="flex">
          <HomeWidgetContainer
            deleteWidget={deleteWidget}
            reference={holderRef}
            layout={homeState?.layout}
            setHomeState={setHomeState}
            onLayoutChange={onLayoutChange}
            modulePermission={modulePermission}
          />
        </WidgetExplorer>
      )}
      <Sheet open={widgetLibraryActive} onClose={toggleWidgetLibrary}>
        <WidgetLibrary
          layout={homeState?.layout}
          addWidget={addWidget}
          toggleWidgetLibrary={toggleWidgetLibrary}
        />
      </Sheet>
    </HomeScreen>
  );
}

function HomeNoData({ intl }) {
  return (
    <NoDataWrapper>
      <NoData
        imageURL={`url(${envConfig.imageRoot}svg/widgets.svg)`}
        text={getTranslatedText(intl, 'alert.noWidgetsFound.heading')}
        description={getTranslatedText(intl, 'alert.noWidgetsFound.desc')}
      />
    </NoDataWrapper>
  );
}

export default withRouter(HomePage);
